import {
  Button,
  Heading,
  Popover,
  Portal,
  useDisclosure
} from "@chakra-ui/react"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { useEffect } from "react"
import {
  AUGMENTATION_PANEL_MENU_CONTENT_PROPS,
  AUGMENTATION_PANEL_MENU_TOOLBAR_CSS,
  AUGMENTATION_PANEL_MENU_TRIGGER_PROPS
} from "./augmentationPanelStyles"
import type { AugmentationToolbarHookReturn } from "./augmentationToolbarTypes"
import AugmentationToolbarView from "./AugmentationToolbarView"
import { canRenderToolbarOption } from "./augmentationToolbarVisibility"
import { useOptionLabel } from "./useAugmentationToolbar"

export function AugmentationToolbarDropdown(
  toolbar: AugmentationToolbarHookReturn
) {
  const { options, selectedKey } = toolbar
  const selectedOption = options.find((option) => option.key === selectedKey)
  const label = useOptionLabel(selectedOption)
  const hasMenuOptions = options.some((option) =>
    canRenderToolbarOption(option, { detailed: true })
  )

  // Close the popover on navigation or when its menu becomes empty.
  const disclosure = useDisclosure()
  useEffect(() => {
    if (disclosure.open && selectedKey) disclosure.onClose()
  }, [selectedKey, hasMenuOptions])

  if (selectedOption && !hasMenuOptions) {
    return (
      <Heading
        as="h2"
        display="flex"
        color="augmentationPanel.text"
        fontSize="16px"
        fontWeight={600}
        lineHeight="19px"
        m={0}
      >
        {label}
      </Heading>
    )
  }

  return selectedOption ? (
    <Popover.Root
      positioning={{ placement: "bottom-start" }}
      open={disclosure.open}
      onOpenChange={(e: { open: boolean }) =>
        e.open ? disclosure.onOpen() : disclosure.onClose()
      }
    >
      {/* The heading wraps the trigger so it reads as a heading containing a
          button, rather than a button that happens to contain a heading. */}
      <Heading
        as="h2"
        display="flex"
        fontSize="16px"
        fontWeight={600}
        lineHeight="19px"
        m={0}
      >
        <Popover.Trigger asChild>
          <Button
            {...AUGMENTATION_PANEL_MENU_TRIGGER_PROPS}
            variant="ghost"
            fontFamily="inherit"
            fontSize="inherit"
            fontWeight="inherit"
            gap="5px"
            lineHeight="inherit"
          >
            <span>{label}</span>
            <Pathicon
              icon="chevron-down"
              size={12}
              opacity={0.6}
              transition="opacity 0.15s ease"
            />
          </Button>
        </Popover.Trigger>
      </Heading>
      <Portal>
        <Popover.Positioner>
          <Popover.Content {...AUGMENTATION_PANEL_MENU_CONTENT_PROPS}>
            <Popover.Body p={0}>
              <AugmentationToolbarView
                toolbar={toolbar}
                detailed
                direction="column"
                alwaysVisible
                backdropFilter="none"
                bg="transparent"
                gap="5px"
                css={AUGMENTATION_PANEL_MENU_TOOLBAR_CSS}
              />
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  ) : null
}
